/**
 * User-Friendly Error Message Utility
 *
 * Maps technical errors to user-friendly messages for display in UI components.
 * This utility provides consistent, actionable error messages across the application.
 *
 * Usage Example:
 * ```javascript
 * import { getUserFriendlyMessage } from './utils/errorMessages';
 *
 * try {
 *   await api.post('/resource', data);
 * } catch (error) {
 *   const friendlyError = getUserFriendlyMessage(error);
 *   showNotification({
 *     title: friendlyError.title,
 *     message: friendlyError.message,
 *     severity: friendlyError.severity
 *   });
 *
 *   if (friendlyError.action === 'redirect_to_login') {
 *     navigate('/login');
 *   }
 * }
 * ```
 */

import { CircuitBreakerError } from '../auth/circuitBreaker';
import { TokenValidationError } from './tokenValidator';

/**
 * Extract the message the backend put in the response body, if any.
 *
 * Our APIs return validation / business-rule failures (e.g. a 400 BadRequest or a
 * 403 "payments are not allowed") as either a raw string body or an object shaped
 * like `{ Message: '...' }` / `{ message: '...' }`. This pulls that out so the UI can
 * show the real reason instead of a generic "Something went wrong".
 *
 * @param {Error} error - Axios error
 * @returns {string|null} The backend-provided message, or null if none is present
 */
export function getBackendErrorMessage(error) {
  const data = error?.response?.data;
  if (!data) {
    return null;
  }
  if (typeof data === 'string') {
    return data.trim() || null;
  }
  return data.Message || data.message || null;
}

/**
 * Map technical errors to user-friendly messages
 *
 * @param {Error} error - The error object
 * @returns {Object} User-friendly error information
 * @returns {string} return.title - Error title for display
 * @returns {string} return.message - User-friendly error message
 * @returns {boolean} return.canRetry - Whether the user can retry the action
 * @returns {string} [return.action] - Optional action to take (e.g., 'redirect_to_login')
 * @returns {number} [return.retryAfter] - Seconds to wait before retry (optional)
 * @returns {string} return.severity - Error severity: 'error', 'warning', or 'info'
 */
export function getUserFriendlyMessage(error) {
  // Circuit breaker errors - service temporarily unavailable
  if (error?.code === 'CIRCUIT_OPEN' || error instanceof CircuitBreakerError) {
    return {
      title: 'Service Temporarily Unavailable',
      message: 'We\'re experiencing technical difficulties. Please try again in a few moments.',
      retryAfter: 60, // seconds
      canRetry: true,
      severity: 'warning',
    };
  }

  // Token validation errors - session expired
  if (error instanceof TokenValidationError) {
    return {
      title: 'Session Expired',
      message: 'Your session has ended. Please log in again.',
      canRetry: false,
      action: 'redirect_to_login',
      severity: 'info',
    };
  }

  // Network timeout errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return {
      title: 'Connection Timeout',
      message: 'The request took too long. Please check your internet connection and try again.',
      canRetry: true,
      severity: 'warning',
    };
  }

  // Network/connection errors
  if (error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error')) {
    return {
      title: 'Connection Error',
      message: 'Unable to connect to the server. Please check your internet connection.',
      canRetry: true,
      severity: 'error',
    };
  }

  // 401 Unauthorized (after retries exhausted)
  if (error?.response?.status === 401) {
    return {
      title: 'Authentication Required',
      message: 'Your session has expired. Please log in again.',
      canRetry: false,
      action: 'redirect_to_login',
      severity: 'info',
    };
  }

  // 403 Forbidden
  if (error?.response?.status === 403) {
    return {
      title: 'Access Denied',
      message: 'You don\'t have permission to access this resource.',
      canRetry: false,
      severity: 'error',
    };
  }

  // 404 Not Found
  if (error?.response?.status === 404) {
    return {
      title: 'Not Found',
      message: 'The requested resource could not be found.',
      canRetry: false,
      severity: 'warning',
    };
  }

  // 429 Too Many Requests
  if (error?.response?.status === 429) {
    const retryAfter = error?.response?.headers?.['retry-after'] || 60;
    return {
      title: 'Too Many Requests',
      message: `Please wait a moment before trying again.`,
      retryAfter: parseInt(retryAfter, 10),
      canRetry: true,
      severity: 'warning',
    };
  }

  // 500-599 Server Errors
  if (error?.response?.status >= 500 && error?.response?.status < 600) {
    return {
      title: 'Server Error',
      message: 'A server error occurred. Our team has been notified. Please try again later.',
      canRetry: true,
      retryAfter: 30,
      severity: 'error',
    };
  }

  // Generic fallback for unknown errors
  return {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred. Please try again.',
    canRetry: true,
    severity: 'error',
  };
}

/**
 * Check if an error is retriable
 *
 * @param {Error} error - The error object
 * @returns {boolean} Whether the error is retriable
 */
export function isRetriableError(error) {
  const friendlyMessage = getUserFriendlyMessage(error);
  return friendlyMessage.canRetry;
}

/**
 * Get retry delay in milliseconds for an error
 *
 * @param {Error} error - The error object
 * @returns {number} Milliseconds to wait before retry (0 if not retriable)
 */
export function getRetryDelay(error) {
  const friendlyMessage = getUserFriendlyMessage(error);
  return friendlyMessage.canRetry && friendlyMessage.retryAfter
    ? friendlyMessage.retryAfter * 1000
    : 0;
}

/**
 * Format error for logging (strips sensitive data)
 *
 * @param {Error} error - The error object
 * @returns {Object} Safe error object for logging
 */
export function formatErrorForLogging(error) {
  return {
    message: error?.message || 'Unknown error',
    code: error?.code,
    status: error?.response?.status,
    statusText: error?.response?.statusText,
    name: error?.name,
    // Explicitly exclude sensitive data:
    // - No tokens
    // - No authorization headers
    // - No request/response bodies
  };
}
